import React, { Component } from 'react';
import { Row, Col, Card, Button, Select, Table, Tag, message } from 'antd';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import AiPromptService, {
    IAiPromptFeedback,
    IAiPromptFeedbackSummary,
    IAiPromptSceneOption,
} from '../../service/ai/AiPromptService';
import './page-settings.less';
import { formatDateTime } from '../../utils/v2';

interface PromptFeedbackPageState {
    feedbackLoading: boolean;
    summaryLoading: boolean;
    summarizeLoading: boolean;
    sceneCode?: string;
    processed?: number;
    feedbackList: IAiPromptFeedback[];
    feedbackPageNo: number;
    feedbackPageSize: number;
    feedbackTotal: number;
    summaryList: IAiPromptFeedbackSummary[];
    summaryPageNo: number;
    summaryPageSize: number;
    summaryTotal: number;
    sceneOptions: IAiPromptSceneOption[];
}

const PROCESSED_OPTIONS = [
    { label: '全部', value: -1 },
    { label: '待处理', value: 0 },
    { label: '已处理', value: 1 },
];

class PromptFeedbackPage extends Component<any, PromptFeedbackPageState> {
    state: PromptFeedbackPageState = {
        feedbackLoading: false,
        summaryLoading: false,
        summarizeLoading: false,
        sceneCode: undefined,
        processed: -1,
        feedbackList: [],
        feedbackPageNo: 1,
        feedbackPageSize: 10,
        feedbackTotal: 0,
        summaryList: [],
        summaryPageNo: 1,
        summaryPageSize: 10,
        summaryTotal: 0,
        sceneOptions: [],
    };

    async componentDidMount() {
        await this.loadSceneOptions();
        await this.fetchFeedback(1, this.state.feedbackPageSize);
        await this.fetchSummary(1, this.state.summaryPageSize);
    }

    loadSceneOptions = async () => {
        const response = await AiPromptService.getPromptTemplateList({}, 1, 1);
        if (response && response.success) {
            this.setState({ sceneOptions: response?.data?.sceneOptions || [] });
        }
    };

    getSceneName = (sceneCode: string) => {
        const target = this.state.sceneOptions.find((item) => item.code === sceneCode);
        return target ? target.name : sceneCode;
    };

    buildFeedbackQuery = () => ({
        sceneCode: this.state.sceneCode,
        processed: this.state.processed === -1 ? undefined : this.state.processed,
    });

    fetchFeedback = async (pageNo: number, pageSize: number) => {
        this.setState({ feedbackLoading: true });
        const response = await AiPromptService.getPromptFeedbackList(this.buildFeedbackQuery(), pageNo, pageSize);
        if (response && response.success) {
            const pageData = response?.data?.feedbackList || {};
            this.setState({
                feedbackList: pageData.records || [],
                feedbackTotal: pageData.total || 0,
                feedbackPageNo: pageData.current || pageNo,
                feedbackPageSize: pageData.size || pageSize,
            });
        } else {
            message.error(response ? response.message : '获取反馈列表失败');
        }
        this.setState({ feedbackLoading: false });
    };

    fetchSummary = async (pageNo: number, pageSize: number) => {
        this.setState({ summaryLoading: true });
        const response = await AiPromptService.getPromptFeedbackSummaryList(this.state.sceneCode, pageNo, pageSize);
        if (response && response.success) {
            const pageData = response?.data?.summaryList || {};
            this.setState({
                summaryList: pageData.records || [],
                summaryTotal: pageData.total || 0,
                summaryPageNo: pageData.current || pageNo,
                summaryPageSize: pageData.size || pageSize,
            });
        } else {
            message.error(response ? response.message : '获取反馈总结失败');
        }
        this.setState({ summaryLoading: false });
    };

    handleSummarize = async () => {
        this.setState({ summarizeLoading: true });
        const response = await AiPromptService.summarizePromptFeedback(this.state.sceneCode);
        if (response && response.success) {
            const processedCount = response?.data?.processedCount || 0;
            message.success(processedCount > 0 ? `已总结 ${processedCount} 条反馈` : (response.message || '已完成反馈总结'));
            await this.fetchFeedback(1, this.state.feedbackPageSize);
            await this.fetchSummary(1, this.state.summaryPageSize);
        } else {
            message.error(response ? response.message : '反馈总结失败');
        }
        this.setState({ summarizeLoading: false });
    };

    parseIssueTypes = (record: IAiPromptFeedbackSummary): Array<{ type: string; count?: number; description?: string }> => {
        const parseArray = (raw: string | undefined) => {
            if (!raw) {
                return [] as Array<{ type: string; count?: number; description?: string }>;
            }
            try {
                const data = JSON.parse(raw);
                if (!Array.isArray(data)) {
                    return [];
                }
                return data
                    .map((item: any) => ({
                        type: (item?.type || '').toString().trim(),
                        count: Number.isFinite(Number(item?.count)) ? Number(item.count) : undefined,
                        description: (item?.description || '').toString().trim() || undefined,
                    }))
                    .filter((item) => !!item.type);
            } catch (e) {
                return [];
            }
        };

        const fromIssueTypesJson = parseArray(record.issueTypesJson);
        if (fromIssueTypesJson.length > 0) {
            return fromIssueTypesJson;
        }

        if (record.summaryContent) {
            try {
                const parsedSummary = JSON.parse(record.summaryContent);
                if (parsedSummary && typeof parsedSummary === 'object') {
                    return parseArray(JSON.stringify(parsedSummary.issueTypes || []));
                }
            } catch (e) {
                // ignore
            }
        }
        return [];
    };

    render() {
        const feedbackColumns: any[] = [
            { title: 'ID', dataIndex: 'id', width: 80 },
            {
                title: '场景',
                dataIndex: 'sceneCode',
                width: 220,
                render: (sceneCode: string) => this.getSceneName(sceneCode),
            },
            { title: '标签', dataIndex: 'tagLabel', width: 180 },
            { title: '提示词版本', dataIndex: 'promptVersion', width: 110 },
            { title: '置信度', dataIndex: 'confidenceScore', width: 100, render: (score: number) => score ? `${score}/10` : '-' },
            { title: '数据源摘要', dataIndex: 'dataSourceSummary', ellipsis: true },
            { title: '推理摘要', dataIndex: 'reasoningSummary', ellipsis: true },
            { title: '不通过理由', dataIndex: 'reason', ellipsis: true },
            { title: '改善建议', dataIndex: 'suggestion', ellipsis: true },
            {
                title: '处理状态',
                dataIndex: 'processed',
                width: 100,
                render: (processed: number) => processed === 1 ? <Tag color="green">已处理</Tag> : <Tag color="gold">待处理</Tag>,
            },
            { title: '反馈时间', dataIndex: 'createTime', width: 170, render: formatDateTime },
        ];

        const summaryColumns: any[] = [
            { title: 'ID', dataIndex: 'id', width: 80 },
            {
                title: '场景',
                dataIndex: 'sceneCode',
                width: 220,
                render: (sceneCode: string) => this.getSceneName(sceneCode),
            },
            { title: '反馈条数', dataIndex: 'sourceFeedbackCount', width: 100 },
            {
                title: '状态',
                dataIndex: 'status',
                width: 100,
                render: (status: number) => status === 1 ? <Tag color="green">已采纳</Tag> : (status === 2 ? <Tag>已忽略</Tag> : <Tag color="blue">新建</Tag>),
            },
            {
                title: '问题类型',
                width: 260,
                render: (_: any, record: IAiPromptFeedbackSummary) => {
                    const issueTypes = this.parseIssueTypes(record);
                    if (issueTypes.length === 0) {
                        return '-';
                    }
                    return (
                        <div>
                            {issueTypes.map((item, index) => (
                                <div key={`${record.id}-${item.type}-${index}`}>
                                    <Tag color="gold">{item.type}</Tag>
                                    {item.count !== undefined ? ` ${item.count}` : ''}
                                    {item.description ? ` - ${item.description}` : ''}
                                </div>
                            ))}
                        </div>
                    );
                },
            },
            {
                title: '总结内容',
                dataIndex: 'summaryContent',
                render: (summaryContent: string) => <div className="settings-summary-pre">{summaryContent || '-'}</div>,
            },
            {
                title: '建议内容',
                dataIndex: 'recommendationContent',
                render: (recommendationContent: string) => <div className="settings-summary-pre">{recommendationContent || '-'}</div>,
            },
            { title: '时间', dataIndex: 'createTime', width: 170, render: formatDateTime },
        ];

        return (
            <div className="page-settings">
                <BreadcrumbCustom breads={['系统设置', '提示词反馈迭代']} />
                <Row gutter={5}>
                    <Col md={23}>
                        <Card title="反馈记录与迭代总结" bordered={false}>
                            <div className="settings-filter-row">
                                <div className="settings-filter-item">
                                    <span className="settings-filter-label">场景</span>
                                    <Select
                                        style={{ width: 240 }}
                                        allowClear
                                        value={this.state.sceneCode}
                                        placeholder="全部场景"
                                        onChange={(value) => this.setState({ sceneCode: value })}
                                    >
                                        {this.state.sceneOptions.map((item) => (
                                            <Select.Option key={item.code} value={item.code}>{item.name}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="settings-filter-item">
                                    <span className="settings-filter-label">状态</span>
                                    <Select
                                        style={{ width: 160 }}
                                        value={this.state.processed}
                                        onChange={(value) => this.setState({ processed: value })}
                                    >
                                        {PROCESSED_OPTIONS.map((item) => (
                                            <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="settings-filter-actions">
                                    <Button
                                        onClick={async () => {
                                            await this.fetchFeedback(1, this.state.feedbackPageSize);
                                            await this.fetchSummary(1, this.state.summaryPageSize);
                                        }}
                                    >
                                        查询
                                    </Button>
                                    <Button
                                        type="primary"
                                        loading={this.state.summarizeLoading}
                                        onClick={this.handleSummarize}
                                    >
                                        总结待处理反馈
                                    </Button>
                                </div>
                            </div>

                            <Table
                                rowKey="id"
                                loading={this.state.feedbackLoading}
                                columns={feedbackColumns}
                                dataSource={this.state.feedbackList}
                                scroll={{ x: 1300 }}
                                pagination={{
                                    current: this.state.feedbackPageNo,
                                    pageSize: this.state.feedbackPageSize,
                                    total: this.state.feedbackTotal,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                }}
                                onChange={(pagination) => {
                                    this.fetchFeedback(pagination.current || 1, pagination.pageSize || this.state.feedbackPageSize);
                                }}
                            />

                            <div style={{ marginTop: 20 }}>
                                <Table
                                    rowKey="id"
                                    loading={this.state.summaryLoading}
                                    columns={summaryColumns}
                                    dataSource={this.state.summaryList}
                                    scroll={{ x: 1500 }}
                                    pagination={{
                                        current: this.state.summaryPageNo,
                                        pageSize: this.state.summaryPageSize,
                                        total: this.state.summaryTotal,
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                    }}
                                    onChange={(pagination) => {
                                        this.fetchSummary(pagination.current || 1, pagination.pageSize || this.state.summaryPageSize);
                                    }}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default PromptFeedbackPage;
